import React, { useState, useEffect, useMemo } from "react";
import { Download, Search, ChevronUp, ChevronDown, Loader2, Table2, Users, UserPlus, Shield, UserCheck, FileText, Settings2, Tag, ChevronsRight, Printer, Sparkles } from "lucide-react";
import { apiGet } from "@/lib/api";
import { formatCurrency } from "@/lib/commission";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { openUserReport, type ReportMonthRow } from "@/lib/userReport";
import { InfluencerTierBadge } from "./tiers/InfluencerTierBadge";
import { InfluencerTierSelector } from "./tiers/InfluencerTierSelector";

interface MonthData {
  total_sales: number;
  total_sales_after_shipping: number;
  shipping_fees: number;
  commission_rate: number;
  commission_amount: number;
  orders_count: number;
}

interface InfluencerRow {
  user_id: string;
  name: string | null;
  email: string | null;
  role: string | null;
  coupon: string;
  months: Record<string, MonthData>;
  total_withdrawn_pending: number;
  total_withdrawn_completed: number;
}

interface AnalyticsResponse {
  months: string[];
  influencers: InfluencerRow[];
}

type SortDirection = "asc" | "desc";
type MetricMode = "total_sales" | "commission_amount" | "saldo" | "roles";
type RoleType = "admin" | "user" | "colaborador" | "contract" | "micro_nano";

interface RegisteredUser {
  id: string;
  name: string;
  social_media: string;
  coupon: string;
  role: string;
  tier_id: string | null;
}

const PT_MONTHS: Record<string, string> = {
  "01": "Jan",
  "02": "Fev",
  "03": "Mar",
  "04": "Abr",
  "05": "Mai",
  "06": "Jun",
  "07": "Jul",
  "08": "Ago",
  "09": "Set",
  "10": "Out",
  "11": "Nov",
  "12": "Dez",
};

function formatMonthLabel(monthYear: string): string {
  const [year, month] = monthYear.split("-");
  const padded = month.padStart(2, "0");
  return `${PT_MONTHS[padded] || month}/${year.slice(2)}`;
}

const PT_FULL_MONTHS: Record<string, string> = {
  "01": "Janeiro", "02": "Fevereiro", "03": "Março", "04": "Abril",
  "05": "Maio", "06": "Junho", "07": "Julho", "08": "Agosto",
  "09": "Setembro", "10": "Outubro", "11": "Novembro", "12": "Dezembro",
};

function formatFullMonthLabel(monthYear: string): string {
  const [year, month] = monthYear.split("-");
  const padded = month.padStart(2, "0");
  return `${PT_FULL_MONTHS[padded] || month} / ${year}`;
}

export const AdminSalesAnalytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<MetricMode>("total_sales");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [registeredUsers, setRegisteredUsers] = useState<RegisteredUser[]>([]);
  const [showAllMonths, setShowAllMonths] = useState(false);
  const [roleDialogUser, setRoleDialogUser] = useState<RegisteredUser | null>(null);
  const [tierDialogUser, setTierDialogUser] = useState<RegisteredUser | null>(null);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [reportingUserId, setReportingUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [analyticsRes, usersRes] = await Promise.all([
          apiGet("/api/commission-balances/analytics"),
          supabase.from("influencers").select("id, name, social_media, coupon, role, tier_id"),
        ]);

        if (analyticsRes.ok) {
          const json: AnalyticsResponse = await analyticsRes.json();
          setData(json);
        } else {
          throw new Error("Erro ao carregar analytics");
        }

        if (!usersRes.error && usersRes.data) {
          setRegisteredUsers(usersRes.data as RegisteredUser[]);
        }
      } catch (err) {
        console.error("Analytics fetch error:", err);
        setError("Erro ao carregar dados de analytics.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const updateUserRole = async (userId: string, newRole: RoleType) => {
    setUpdatingRole(true);
    try {
      const { error } = await supabase.from("influencers").update({ role: newRole }).eq("id", userId);
      if (error) {
        toast.error(`Erro ao atualizar role: ${error.message}`);
      } else {
        toast.success("Role atualizada com sucesso!");
        setRegisteredUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
        setRoleDialogUser(null);
      }
    } catch {
      toast.error("Erro inesperado");
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleGenerateReport = async (inf: InfluencerRow) => {
    if (!data) return;
    setReportingUserId(inf.user_id);
    try {
      // Pick the 3 most recent calendar months (current + 2 previous)
      const now = new Date();
      const ymKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const recentKeys = [
        ymKey(new Date(now.getFullYear(), now.getMonth() - 2, 1)),
        ymKey(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        ymKey(now),
      ];

      const monthRows: ReportMonthRow[] = recentKeys
        .slice()
        .reverse() // most recent first in the report table
        .map((mk) => {
          const m = inf.months[mk];
          return {
            monthYear: mk,
            label: formatFullMonthLabel(mk),
            totalSales: m?.total_sales || 0,
            commission: m?.commission_amount || 0,
            ordersCount: m?.orders_count || 0,
          };
        });

      const totalSales = monthRows.reduce((s, m) => s + m.totalSales, 0);
      const totalCommission = monthRows.reduce((s, m) => s + m.commission, 0);

      // Fetch most recent withdrawal request for CPF (stored in cnpj column)
      const { data: wrs } = await supabase
        .from("withdrawal_requests")
        .select("cnpj, created_at")
        .eq("user_id", inf.user_id)
        .order("created_at", { ascending: false })
        .limit(1);

      const cpf = wrs && wrs.length > 0 ? (wrs[0].cnpj as string) : null;

      openUserReport({
        name: inf.name || "—",
        coupon: inf.coupon || "",
        cpf,
        months: monthRows,
        totalSales,
        totalCommission,
      });

      if (!cpf) {
        toast.warning("CPF não encontrado — o relatório foi gerado sem este dado. Peça ao parceiro que registre um saque.");
      }
    } catch (err) {
      console.error("Report error:", err);
      const message = err instanceof Error ? err.message : "Erro ao gerar relatório";
      toast.error(message);
    } finally {
      setReportingUserId(null);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "admin":
        return <Badge variant="destructive" className="text-xs"><Shield className="h-3 w-3 mr-1" />Admin</Badge>;
      case "contract":
        return <Badge className="text-xs bg-orange-600 text-white"><FileText className="h-3 w-3 mr-1" />Contrato</Badge>;
      case "user":
        return <Badge className="text-xs bg-orange-500 text-white"><Users className="h-3 w-3 mr-1" />Influencer</Badge>;
      case "colaborador":
        return <Badge className="text-xs bg-orange-400 text-white"><UserCheck className="h-3 w-3 mr-1" />Colaborador</Badge>;
      case "micro_nano":
        return <Badge className="text-xs bg-purple-500 text-white"><Sparkles className="h-3 w-3 mr-1" />Micro e Nano</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">Sem role</Badge>;
    }
  };

  const getVal = (inf: InfluencerRow, month: string): number => {
    const m = inf.months[month];
    if (!m) return 0;
    return metric === "total_sales" ? m.total_sales : m.commission_amount;
  };

  const getRowTotal = (inf: InfluencerRow): number => {
    if (!data) return 0;
    return data.months.reduce((sum, month) => sum + getVal(inf, month), 0);
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase().trim();
    return data.influencers.filter((inf) => {
      if ((inf.coupon || "").toUpperCase() === "SILVA") return false;
      if (!q) return true;
      return (
        (inf.name || "").toLowerCase().includes(q) ||
        (inf.coupon || "").toLowerCase().includes(q)
      );
    });
  }, [data, search]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    rows.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (sortKey === "name") {
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
      } else if (sortKey === "coupon") {
        aVal = (a.coupon || "").toLowerCase();
        bVal = (b.coupon || "").toLowerCase();
      } else if (sortKey === "total") {
        aVal = getRowTotal(a);
        bVal = getRowTotal(b);
      } else {
        // sortKey is a month_year
        aVal = getVal(a, sortKey);
        bVal = getVal(b, sortKey);
      }

      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [filtered, sortKey, sortDir, metric, data]);

  const colTotals = useMemo(() => {
    if (!data) return {};
    const totals: Record<string, number> = {};
    for (const month of data.months) {
      totals[month] = filtered.reduce((sum, inf) => sum + getVal(inf, month), 0);
    }
    return totals;
  }, [data, filtered, metric]);

  const grandTotal = useMemo(() => {
    if (!data) return 0;
    return filtered.reduce((sum, inf) => sum + getRowTotal(inf), 0);
  }, [data, filtered, metric]);

  // Full ordered list of month columns (ascending). Always spans from the
  // earliest month that has data (or the current month) up to and including the
  // current month, with no gaps — so the current month and every month before
  // it always appears as a column, even before its commissions are calculated.
  const monthColumns = useMemo(() => {
    const dataMonths = data?.months ?? [];
    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    // Start from the earliest of the data months and the current month.
    const startKey = [...dataMonths, currentKey].sort()[0];
    let [y, m] = startKey.split("-").map(Number);
    const [cy, cm] = currentKey.split("-").map(Number);

    const result: string[] = [];
    const seen = new Set<string>();
    // Walk month-by-month from earliest up to (and including) the current month.
    while (y < cy || (y === cy && m <= cm)) {
      const key = `${y}-${String(m).padStart(2, "0")}`;
      result.push(key);
      seen.add(key);
      m++;
      if (m > 12) { m = 1; y++; }
    }
    // Keep any data months that fall outside the generated range (e.g. future-dated rows).
    for (const dm of dataMonths) {
      if (!seen.has(dm)) result.push(dm);
    }
    result.sort();
    return result;
  }, [data]);

  // Balance computations for "Saldo" tab
  const getInfluencerTotalCommissions = (inf: InfluencerRow): number => {
    if (!data) return 0;
    return data.months.reduce((sum, month) => {
      const m = inf.months[month];
      return sum + (m ? m.commission_amount : 0);
    }, 0);
  };

  const getInfluencerTotalWithdrawn = (inf: InfluencerRow): number => {
    return (inf.total_withdrawn_pending || 0) + (inf.total_withdrawn_completed || 0);
  };

  const getInfluencerAvailable = (inf: InfluencerRow): number => {
    return Math.max(0, getInfluencerTotalCommissions(inf) - getInfluencerTotalWithdrawn(inf));
  };

  const balanceSorted = useMemo(() => {
    const rows = [...filtered];
    rows.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (sortKey === "name") {
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
      } else if (sortKey === "coupon") {
        aVal = (a.coupon || "").toLowerCase();
        bVal = (b.coupon || "").toLowerCase();
      } else if (sortKey === "commissions") {
        aVal = getInfluencerTotalCommissions(a);
        bVal = getInfluencerTotalCommissions(b);
      } else if (sortKey === "withdrawn") {
        aVal = getInfluencerTotalWithdrawn(a);
        bVal = getInfluencerTotalWithdrawn(b);
      } else {
        // "available" or default
        aVal = getInfluencerAvailable(a);
        bVal = getInfluencerAvailable(b);
      }

      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [filtered, sortKey, sortDir, data]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon: React.FC<{ columnKey: string }> = ({ columnKey }) => {
    if (sortKey !== columnKey) return <ChevronDown className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-40 transition-opacity" />;
    return sortDir === "asc" ? (
      <ChevronUp className="w-3 h-3 ml-1 text-primary" />
    ) : (
      <ChevronDown className="w-3 h-3 ml-1 text-primary" />
    );
  };

  const exportToExcel = () => {
    if (!data) {
      toast.error("Não há dados para exportar");
      return;
    }

    const wb = XLSX.utils.book_new();

    if (metric === "saldo") {
      const rows = balanceSorted.map((inf) => ({
        Nome: inf.name || "",
        Cupom: inf.coupon || "",
        "Total Comissões": getInfluencerTotalCommissions(inf),
        "Saques (Pago)": inf.total_withdrawn_completed || 0,
        "Saques (Pendente)": inf.total_withdrawn_pending || 0,
        "Disponível": getInfluencerAvailable(inf),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 25 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, ws, "Saldo");

      const fileName = `analytics_saldo_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } else {
      if (!sorted.length) {
        toast.error("Não há dados para exportar");
        return;
      }

      const monthsDesc = [...monthColumns].reverse();

      const rows = sorted.map((inf) => {
        const row: Record<string, string | number> = {
          Nome: inf.name || "",
          Cupom: inf.coupon || "",
          Total: getRowTotal(inf),
        };
        for (const month of monthsDesc) {
          const label = formatMonthLabel(month);
          row[label] = getVal(inf, month);
        }
        return row;
      });

      // Add totals row
      const totalsRow: Record<string, string | number> = {
        Nome: "TOTAL",
        Cupom: "",
        Total: grandTotal,
      };
      for (const month of monthsDesc) {
        totalsRow[formatMonthLabel(month)] = colTotals[month] || 0;
      }
      rows.push(totalsRow);

      const ws = XLSX.utils.json_to_sheet(rows);
      const colWidths = [
        { wch: 25 }, // Nome
        { wch: 12 }, // Cupom
        { wch: 14 }, // Total
        ...monthsDesc.map(() => ({ wch: 14 })),
      ];
      ws["!cols"] = colWidths;

      const sheetName = metric === "total_sales" ? "Vendas" : "Comissoes";
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const metricLabel = metric === "total_sales" ? "vendas" : "comissoes";
      const fileName = `analytics_${metricLabel}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    }

    toast.success("Exportação concluída com sucesso!");
  };

  // Months in reverse chronological order (most recent first)
  const allReversedMonths = [...monthColumns].reverse();
  const reversedMonths = showAllMonths ? allReversedMonths : allReversedMonths.slice(0, 3);

  // Current month for highlighting
  const now = new Date();
  const currentMonthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthYear = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}`;
  const prev2Month = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const prev2MonthYear = `${prev2Month.getFullYear()}-${String(prev2Month.getMonth() + 1).padStart(2, "0")}`;
  const isRecentMonth = (m: string) => m === currentMonthYear || m === prevMonthYear || m === prev2MonthYear;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-16">
          <div className="flex items-center justify-center space-x-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-muted-foreground">Carregando dados de analytics...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-16">
          <p className="text-center text-muted-foreground">{error || "Nenhum dado encontrado."}</p>
        </CardContent>
      </Card>
    );
  }

  // Stats for summary cards
  const totalRegistered = registeredUsers.filter((u) => (u.coupon || "").toUpperCase() !== "SILVA").length;
  const couponsWithSales = new Set(
    (data?.influencers || [])
      .filter((i) => {
        const totalSales = data?.months.reduce((sum, m) => sum + (i.months[m]?.total_sales || 0), 0) || 0;
        return totalSales > 0;
      })
      .map((i) => i.coupon.toUpperCase())
  );
  const zeroSalesCount = registeredUsers.filter(
    (u) => (u.coupon || "").toUpperCase() !== "SILVA" && !couponsWithSales.has((u.coupon || "").toUpperCase())
  ).length;

  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthKey = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}`;
  const recentSellersCount = (data?.influencers || []).filter((i) => {
    if ((i.coupon || "").toUpperCase() === "SILVA") return false;
    const cur = i.months[currentMonthYear];
    const prev = i.months[lastMonthKey];
    return (cur && cur.total_sales > 0) || (prev && prev.total_sales > 0);
  }).length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <Users className="h-4 w-4" /> Registradas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg sm:text-2xl font-bold">{totalRegistered}</p>
            <p className="text-xs text-muted-foreground">Influenciadoras</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <UserPlus className="h-4 w-4" /> Novas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg sm:text-2xl font-bold text-primary">{totalRegistered - zeroSalesCount}</p>
            <p className="text-xs text-muted-foreground">Com vendas</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Sem vendas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg sm:text-2xl font-bold text-red-400">{zeroSalesCount}</p>
            <p className="text-xs text-muted-foreground">Zero vendas totais</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">Vendas recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg sm:text-2xl font-bold text-green-400">{recentSellersCount}</p>
            <p className="text-xs text-muted-foreground">Venderam nos últimos meses</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Table2 className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-bold">Analytics</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-white/10 overflow-hidden">
            <button
              onClick={() => setMetric("total_sales")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                metric === "total_sales"
                  ? "bg-primary text-primary-foreground"
                  : "bg-white/5 hover:bg-white/10"
              }`}
            >
              Vendas
            </button>
            <button
              onClick={() => setMetric("commission_amount")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                metric === "commission_amount"
                  ? "bg-primary text-primary-foreground"
                  : "bg-white/5 hover:bg-white/10"
              }`}
            >
              Comissões
            </button>
            <button
              onClick={() => { setMetric("saldo"); setSortKey("available"); setSortDir("desc"); }}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                metric === "saldo"
                  ? "bg-primary text-primary-foreground"
                  : "bg-white/5 hover:bg-white/10"
              }`}
            >
              Saldo
            </button>
            <button
              onClick={() => { setMetric("roles"); setSortKey("name"); setSortDir("asc"); }}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                metric === "roles"
                  ? "bg-primary text-primary-foreground"
                  : "bg-white/5 hover:bg-white/10"
              }`}
            >
              Gerenciar
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-40"
            />
          </div>
          {(metric === "total_sales" || metric === "commission_amount") && allReversedMonths.length > 3 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllMonths(!showAllMonths)}
              className="border-white/10 bg-white/5 hover:bg-white/10 text-xs"
            >
              <ChevronsRight className="w-3 h-3 mr-1" />
              {showAllMonths ? "Menos" : `+${allReversedMonths.length - 3} meses`}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportToExcel} className="border-white/10 bg-white/5 hover:bg-white/10">
            <Download className="w-4 h-4 mr-2" />
            XLSX
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {metric === "roles" ? (
            /* Roles management table */
            <div className="overflow-x-auto relative">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th
                      onClick={() => handleSort("name")}
                      className="text-left px-4 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center">
                        Nome
                        <SortIcon columnKey="name" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("coupon")}
                      className="text-left px-4 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center">
                        Cupom
                        <SortIcon columnKey="coupon" />
                      </span>
                    </th>
                    <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                      Instagram
                    </th>
                    <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                      Role
                    </th>
                    <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                      Categoria
                    </th>
                    <th className="text-center px-4 py-3 font-semibold whitespace-nowrap">
                      Alterar
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {registeredUsers
                    .filter((u) => {
                      if ((u.coupon || "").toUpperCase() === "SILVA") return false;
                      const q = search.toLowerCase().trim();
                      if (!q) return true;
                      return (u.name || "").toLowerCase().includes(q) || (u.coupon || "").toLowerCase().includes(q);
                    })
                    .sort((a, b) => {
                      const rolePriority: Record<string, number> = { admin: 1, contract: 2, user: 3, colaborador: 4, micro_nano: 5 };
                      if (sortKey === "name") {
                        const dir = sortDir === "asc" ? 1 : -1;
                        return dir * (a.name || "").localeCompare(b.name || "");
                      }
                      if (sortKey === "coupon") {
                        const dir = sortDir === "asc" ? 1 : -1;
                        return dir * (a.coupon || "").localeCompare(b.coupon || "");
                      }
                      return (rolePriority[a.role] || 5) - (rolePriority[b.role] || 5);
                    })
                    .map((u) => (
                      <tr key={u.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3 font-medium">{u.name || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs">{u.coupon || "—"}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{u.social_media || "—"}</td>
                        <td className="px-4 py-3">{getRoleBadge(u.role)}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setTierDialogUser(u)}
                            title="Alterar categoria"
                            className="transition-opacity hover:opacity-80"
                          >
                            <InfluencerTierBadge tierId={u.tier_id} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setRoleDialogUser(u)}
                            className="text-muted-foreground hover:text-primary transition-colors"
                          >
                            <Settings2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : metric === "saldo" ? (
            /* Balance table */
            <div className="overflow-x-auto relative">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-2 py-3 text-center text-muted-foreground font-medium w-8">#</th>
                    <th
                      onClick={() => handleSort("name")}
                      className="group text-left px-4 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center">
                        Nome
                        <SortIcon columnKey="name" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("coupon")}
                      className="group text-left px-4 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center">
                        Cupom
                        <SortIcon columnKey="coupon" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("commissions")}
                      className="group text-right px-4 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center justify-end">
                        Total Comissões
                        <SortIcon columnKey="commissions" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("withdrawn")}
                      className="group text-right px-4 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center justify-end">
                        Saques
                        <SortIcon columnKey="withdrawn" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("available")}
                      className="group text-right px-4 py-3 font-bold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center justify-end">
                        Disponível
                        <SortIcon columnKey="available" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {balanceSorted.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-muted-foreground">
                        Nenhum resultado encontrado.
                      </td>
                    </tr>
                  ) : (
                    balanceSorted.map((inf, idx) => {
                      const totalComm = getInfluencerTotalCommissions(inf);
                      const totalWith = getInfluencerTotalWithdrawn(inf);
                      const available = getInfluencerAvailable(inf);
                      return (
                        <tr key={inf.user_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-2 py-2.5 text-center text-muted-foreground/50 text-xs tabular-nums">{idx + 1}</td>
                          <td className="px-4 py-2.5 font-medium max-w-[240px]">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleGenerateReport(inf)}
                                disabled={reportingUserId === inf.user_id}
                                title="Gerar relatório (3 meses)"
                                className="text-muted-foreground/60 hover:text-primary transition-colors flex-shrink-0 disabled:opacity-50"
                              >
                                {reportingUserId === inf.user_id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Printer className="h-3.5 w-3.5" />
                                )}
                              </button>
                              <span className="truncate">{inf.name || "—"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 border-r">
                            <span className="inline-flex items-center gap-1.5">
                              <Tag className="h-3 w-3 text-primary" />
                              <span className="font-bold tracking-wider uppercase text-xs">{inf.coupon || "—"}</span>
                            </span>
                          </td>
                          <td className="text-right px-4 py-2.5 tabular-nums">
                            {formatCurrency(totalComm)}
                          </td>
                          <td className="text-right px-4 py-2.5 tabular-nums">
                            {totalWith > 0 ? (
                              <span title={`Pago: ${formatCurrency(inf.total_withdrawn_completed || 0)} | Pendente: ${formatCurrency(inf.total_withdrawn_pending || 0)}`}>
                                {formatCurrency(totalWith)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground/40">{formatCurrency(0)}</span>
                            )}
                          </td>
                          <td className={`text-right px-4 py-2.5 font-semibold tabular-nums ${
                            available === 0 ? "text-muted-foreground/40" : available > 0 ? "text-green-400" : "text-red-400"
                          }`}>
                            {formatCurrency(available)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {balanceSorted.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-white/10 font-semibold">
                      <td className="px-2 py-3"></td>
                      <td className="px-4 py-3">TOTAL</td>
                      <td className="px-4 py-3 border-r"></td>
                      <td className="text-right px-4 py-3 tabular-nums">
                        {formatCurrency(balanceSorted.reduce((s, i) => s + getInfluencerTotalCommissions(i), 0))}
                      </td>
                      <td className="text-right px-4 py-3 tabular-nums">
                        {formatCurrency(balanceSorted.reduce((s, i) => s + getInfluencerTotalWithdrawn(i), 0))}
                      </td>
                      <td className="text-right px-4 py-3 font-bold tabular-nums text-green-400">
                        {formatCurrency(balanceSorted.reduce((s, i) => s + getInfluencerAvailable(i), 0))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            /* Month-by-month table (Vendas / Comissao) */
            /* Wrapper: scaleY(-1) on outer flips scrollbar to top, scaleY(-1) on inner flips content back */
            <div className="overflow-x-auto relative" style={{ transform: "scaleY(-1)" }}>
              <div style={{ transform: "scaleY(-1)" }}>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-2 py-3 text-center text-muted-foreground font-medium w-8">#</th>
                    <th
                      onClick={() => handleSort("name")}
                      className="group text-left px-4 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center">
                        Nome
                        <SortIcon columnKey="name" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort("coupon")}
                      className="group text-left px-2 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center">
                        Cupom
                        <SortIcon columnKey="coupon" />
                      </span>
                    </th>
                    {showAllMonths && (
                      <th
                        onClick={() => handleSort("total")}
                        className="group text-right px-4 py-3 font-bold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap border-r"
                      >
                        <span className="inline-flex items-center justify-end">
                          Total
                          <SortIcon columnKey="total" />
                        </span>
                      </th>
                    )}
                    {reversedMonths.map((month) => (
                      <th
                        key={month}
                        onClick={() => handleSort(month)}
                        className={`group text-right px-4 py-3 font-semibold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap ${
                          isRecentMonth(month) ? "bg-primary/10 text-primary" : ""
                        }`}
                      >
                        <span className="inline-flex items-center justify-end">
                          {formatMonthLabel(month)}
                          <SortIcon columnKey={month} />
                        </span>
                      </th>
                    ))}
                    {!showAllMonths && (
                      <th
                        onClick={() => handleSort("total")}
                        className="group text-right px-4 py-3 font-bold cursor-pointer hover:bg-white/5 select-none whitespace-nowrap border-l"
                      >
                        <span className="inline-flex items-center justify-end">
                          Total
                          <SortIcon columnKey="total" />
                        </span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan={reversedMonths.length + 4} className="text-center py-12 text-muted-foreground">
                        Nenhum resultado encontrado.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((inf, idx) => {
                      const rowTotal = getRowTotal(inf);
                      return (
                        <tr key={inf.user_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="px-2 py-2.5 text-center text-muted-foreground/50 text-xs tabular-nums">{idx + 1}</td>
                          <td className="px-4 py-2.5 font-medium max-w-[200px]">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleGenerateReport(inf)}
                                disabled={reportingUserId === inf.user_id}
                                title="Gerar relatório (3 meses)"
                                className="text-muted-foreground/60 hover:text-primary transition-colors flex-shrink-0 disabled:opacity-50"
                              >
                                {reportingUserId === inf.user_id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Printer className="h-3.5 w-3.5" />
                                )}
                              </button>
                              <span className="truncate">{inf.name || "—"}</span>
                            </div>
                          </td>
                          <td className="px-2 py-2.5">
                            <span className="inline-flex items-center gap-1">
                              <Tag className="h-3 w-3 text-primary" />
                              <span className="font-bold tracking-wider uppercase text-xs">{inf.coupon || "—"}</span>
                            </span>
                          </td>
                          {showAllMonths && (
                            <td className="text-right px-4 py-2.5 font-semibold tabular-nums border-r">
                              {formatCurrency(rowTotal)}
                            </td>
                          )}
                          {reversedMonths.map((month) => {
                            const val = getVal(inf, month);
                            const recent = isRecentMonth(month);
                            return (
                              <td
                                key={month}
                                className={`text-right px-4 py-2.5 tabular-nums ${
                                  val === 0
                                    ? "text-muted-foreground/30"
                                    : recent
                                      ? "text-primary font-medium"
                                      : ""
                                } ${recent ? "bg-primary/5" : ""}`}
                              >
                                {formatCurrency(val)}
                              </td>
                            );
                          })}
                          {!showAllMonths && (
                            <td className="text-right px-4 py-2.5 font-semibold tabular-nums border-l">
                              {formatCurrency(rowTotal)}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {sorted.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-white/10 font-semibold">
                      <td className="px-2 py-3"></td>
                      <td className="px-4 py-3">TOTAL</td>
                      <td className="px-2 py-3"></td>
                      {showAllMonths && (
                        <td className="text-right px-4 py-3 font-bold tabular-nums border-r">
                          {formatCurrency(grandTotal)}
                        </td>
                      )}
                      {reversedMonths.map((month) => (
                        <td key={month} className={`text-right px-4 py-3 tabular-nums ${isRecentMonth(month) ? "bg-primary/5 text-primary" : ""}`}>
                          {formatCurrency(colTotals[month] || 0)}
                        </td>
                      ))}
                      {!showAllMonths && (
                        <td className="text-right px-4 py-3 font-bold tabular-nums border-l">
                          {formatCurrency(grandTotal)}
                        </td>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Edit Dialog */}
      <Dialog open={!!roleDialogUser} onOpenChange={(open) => !open && setRoleDialogUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Alterar Role</DialogTitle>
          </DialogHeader>
          {roleDialogUser && (
            <div className="space-y-4">
              <div>
                <p className="font-medium">{roleDialogUser.name}</p>
                <p className="text-sm text-muted-foreground font-mono">{roleDialogUser.coupon}</p>
              </div>
              <div className="space-y-2">
                {(["admin", "contract", "user", "colaborador", "micro_nano"] as RoleType[]).map((role) => (
                  <button
                    key={role}
                    onClick={() => updateUserRole(roleDialogUser.id, role)}
                    disabled={updatingRole || roleDialogUser.role === role}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      roleDialogUser.role === role
                        ? "bg-primary/20 border border-primary"
                        : "bg-white/5 border border-white/10 hover:bg-white/10"
                    } ${updatingRole ? "opacity-50" : ""}`}
                  >
                    {getRoleBadge(role)}
                    {roleDialogUser.role === role && <span className="ml-auto text-xs text-primary">Atual</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tier (Categoria) Edit Dialog */}
      <InfluencerTierSelector
        influencer={tierDialogUser}
        onOpenChange={(open) => !open && setTierDialogUser(null)}
        onChanged={(userId, tierId) => {
          setRegisteredUsers((prev) =>
            prev.map((u) => (u.id === userId ? { ...u, tier_id: tierId } : u))
          );
        }}
      />
    </div>
  );
};

export default AdminSalesAnalytics;
